/**
 * Product Gallery Swiper with Variant-based Image Switching
 *
 * Handles swiper initialization, variant-based image switching and the
 * keyboard / screen-reader behaviour required for ADA compliance:
 *  - Left/Right arrow keys move the gallery while focus is inside it
 *  - Enter / Space activate the prev-next arrows and the thumbnails
 *  - Arrows are real buttons with accessible names and a visible focus ring
 *  - Off-screen slides are taken out of the tab order
 */

document.addEventListener("DOMContentLoaded", function () {

  var KEYBOARD_ONLY_WHEN_FOCUSED = true;

  var FOCUSABLE_SELECTOR =
    'a[href], button, input, select, textarea, video[controls], iframe, model-viewer, [tabindex]';

  let thumbsSwiper = null;
  let mainSwiper = null;
  let thumbTriggers = [];

  function getStrings(galleryEl) {
    const data = (galleryEl && galleryEl.dataset) || {};
    const slideLabel = data.a11ySlideLabel || 'Image [index] of [total]';

    return {
      prev: data.a11yPrev || 'Previous slide',
      next: data.a11yNext || 'Next slide',
      first: data.a11yFirst || 'This is the first image',
      last: data.a11yLast || 'This is the last image',
      carousel: data.a11yCarousel || 'carousel',
      item: data.a11yItem || 'slide',
      thumbLabel: data.a11yThumbLabel || 'Show image [index] of [total]',
      slideLabel: slideLabel
        .replace('[index]', '{{index}}')
        .replace('[total]', '{{slidesLength}}'),
    };
  }

  function fillIn(template, index, total) {
    return template.replace('[index]', index).replace('[total]', total);
  }

  function initializeSwipers() {
    if (typeof Swiper === 'undefined') return;

    const galleryEl = document.querySelector('.product-swiper-gallery');
    const thumbsSwiperEl = document.querySelector('.product-thumbnails-swiper');
    const mainSwiperEl = document.querySelector('.product-main-swiper');
    if (!thumbsSwiperEl || !mainSwiperEl) return;
    if (mainSwiperEl.classList.contains('swiper-initialized')) return;

    const strings = getStrings(galleryEl);

    thumbsSwiper = new Swiper(thumbsSwiperEl, {
      slidesPerView: 4.5,
      spaceBetween: 15,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      a11y: { enabled: false },
      breakpoints: {
        0: {
          spaceBetween: 8,
        },
        640: {
          spaceBetween: 10,
        },
        1024: {
          spaceBetween: 15,
        },
      }
    });

    mainSwiper = new Swiper(mainSwiperEl, {
      slidesPerView: 1,
      spaceBetween: 10,
      autoHeight: true,
      navigation: {
        nextEl: '.product-swiper-next',
        prevEl: '.product-swiper-prev',
      },
      keyboard: {
        enabled: !KEYBOARD_ONLY_WHEN_FOCUSED,
        onlyInViewport: true,
        pageUpDown: false,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: strings.prev,
        nextSlideMessage: strings.next,
        firstSlideMessage: strings.first,
        lastSlideMessage: strings.last,
        containerRoleDescriptionMessage: strings.carousel,
        itemRoleDescriptionMessage: strings.item,
        slideLabelMessage: strings.slideLabel,
      },
      thumbs: {
        swiper: thumbsSwiper,
      },
      on: {
        slideChange: function () {
          handleSlideChange(this);
          updateSlideFocusability(this);
          updateThumbnailState(this);
        },
      },
    });

    setupThumbnailKeyboard(thumbsSwiperEl, mainSwiperEl, strings);
    setupKeyboardScope(mainSwiperEl, galleryEl);
    updateSlideFocusability(mainSwiper);
    updateThumbnailState(mainSwiper);
  }

  function setupKeyboardScope(mainSwiperEl, galleryEl) {
    if (!KEYBOARD_ONLY_WHEN_FOCUSED) return;
    if (!mainSwiper || !mainSwiper.keyboard) return;

    const scope =
      mainSwiperEl.closest('.product-media') || galleryEl || mainSwiperEl;

    scope.addEventListener('focusin', function () {
      mainSwiper.keyboard.enable();
    });

    scope.addEventListener('focusout', function (event) {
      if (!scope.contains(event.relatedTarget)) {
        mainSwiper.keyboard.disable();
      }
    });
  }

  function setupThumbnailKeyboard(thumbsSwiperEl, mainSwiperEl, strings) {
    const slides = Array.prototype.slice.call(
      thumbsSwiperEl.querySelectorAll('.swiper-slide')
    );
    const total = slides.length;

    thumbTriggers = slides.map(function (slide, index) {
      const trigger = slide.querySelector('.media-thumbnail') || slide;

      const listItem = slide.querySelector('.product-thumbnail-list-item');
      if (listItem) {
        listItem.setAttribute('role', 'presentation');
      }

      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
      trigger.setAttribute(
        'aria-label',
        fillIn(strings.thumbLabel, index + 1, total)
      );
      if (mainSwiperEl.id) {
        trigger.setAttribute('aria-controls', mainSwiperEl.id);
      }

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        goToSlide(index);
      });

      trigger.addEventListener('keydown', function (event) {
        const key = event.key;
        if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
          event.preventDefault();
          goToSlide(index);
        }
      });

      trigger.addEventListener('focus', function () {
        if (!thumbsSwiper || thumbsSwiper.destroyed) return;
        const thumbSlide = thumbsSwiper.slides[index];
        if (thumbSlide && thumbSlide.classList.contains('swiper-slide-visible')) {
          return;
        }
        thumbsSwiper.slideTo(index);
      });

      return trigger;
    });
  }

  function goToSlide(index) {
    if (!mainSwiper || mainSwiper.destroyed) return;
    mainSwiper.slideTo(index);
  }

  function updateThumbnailState(swiper) {
    thumbTriggers.forEach(function (trigger, index) {
      if (index === swiper.activeIndex) {
        trigger.setAttribute('aria-current', 'true');
      } else {
        trigger.removeAttribute('aria-current');
      }
    });
  }

  function updateSlideFocusability(swiper) {
    Array.prototype.forEach.call(swiper.slides, function (slide, index) {
      const isActive = index === swiper.activeIndex;
      const focusables = slide.querySelectorAll(FOCUSABLE_SELECTOR);

      Array.prototype.forEach.call(focusables, function (el) {
        if (isActive) {
          if (!el.hasAttribute('data-swiper-tabindex')) return;
          const original = el.getAttribute('data-swiper-tabindex');
          if (original === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', original);
          }
          el.removeAttribute('data-swiper-tabindex');
        } else {
          if (el.hasAttribute('data-swiper-tabindex')) return;
          el.setAttribute(
            'data-swiper-tabindex',
            el.getAttribute('tabindex') === null ? '' : el.getAttribute('tabindex')
          );
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  function handleSlideChange(swiper) {
    const previousIndex = swiper.previousIndex;
    const currentIndex = swiper.activeIndex;
    const slides = swiper.slides;

    if (previousIndex !== undefined && slides[previousIndex]) {
      const prevSlide = slides[previousIndex];
      const prevVideo = prevSlide.querySelector('video');
      if (prevVideo && !prevVideo.paused) {
        prevVideo.pause();
        prevVideo.currentTime = 0;
      }
    }

    const currentSlide = slides[currentIndex];
    const currentVideo = currentSlide.querySelector('video');
    if (currentVideo) {
      currentVideo.play().catch(() => {
        console.log('Autoplay prevented, user interaction required.');
      });
    }
  }

  function handleVariantChange(event) {
    const newVariant = event.detail.variant;
    if (!newVariant || !mainSwiper) return;
    if (newVariant.featured_image) {
      const variantImageId = newVariant.featured_media.id;
      const slides = mainSwiper.slides;
      let targetSlideIndex = -1;
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        const mediaId = slide.getAttribute('data-media-id');
        if (mediaId && parseInt(mediaId) === variantImageId) {
          targetSlideIndex = i;
          break;
        }
      }
      if (targetSlideIndex !== -1) {
        mainSwiper.slideTo(targetSlideIndex);
      }
    }
  }

  initializeSwipers();
  document.addEventListener('theme:variant:change', handleVariantChange);
  if (!thumbsSwiper || !mainSwiper) {
    setTimeout(initializeSwipers, 100);
  }
});
